/**
 * WhyFlaggedDrawer — Slide-in drawer for SHAP explainability
 *
 * Phase 1.5-shap — SHAP explainability surfacing
 *
 * Props: anomaly_id, onClose
 * Tabs:
 *   - 'This anomaly': calls /explain for the anomaly_id, renders ShapBar
 *   - 'Global importance': calls /feature-importance, renders ShapBar for top-N features
 *
 * Design spec: docs/plans/2026-06-25-redesign-design-spec.md
 *   - All colors via CSS vars only
 *   - Dark default; honor data-theme for Daylight mode
 *   - DM Serif/Outfit/DM Mono type stack
 *   - Reuse EmptyState, FinancialValue primitives
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, HelpCircle, BarChart3, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { ShapBar, ShapBarItem } from '@/components/primitives/ShapBar';
import {
  explainAnomaly,
  getFeatureImportance,
  ExplainabilityResponse,
  FeatureImportanceResponse,
} from '@/api/explainabilityApi';

type TabId = 'this-anomaly' | 'global-importance';

interface WhyFlaggedDrawerProps {
  /** Anomaly ID to explain */
  anomalyId: string;
  /** Callback when drawer closes */
  onClose: () => void;
  /** Whether drawer is open */
  isOpen: boolean;
  /** Optional report/case ID for context */
  reportId?: string;
  /** Additional CSS classes */
  className?: string;
}

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const TABS: TabConfig[] = [
  { id: 'this-anomaly', label: 'This anomaly', icon: <Sparkles className="w-4 h-4" /> },
  { id: 'global-importance', label: 'Global importance', icon: <BarChart3 className="w-4 h-4" /> },
];

// ---------------------------------------------------------------------------
// Helper to convert API response to ShapBar items
// ---------------------------------------------------------------------------

function explainabilityToShapItems(response: ExplainabilityResponse): ShapBarItem[] {
  return response.attributions.map((att) => ({
    feature: att.feature,
    attribution: att.attribution,
    description: att.description,
    isMonetary: att.is_monetary,
  }));
}

function featureImportanceToShapItems(response: FeatureImportanceResponse): ShapBarItem[] {
  return response.features.map((feat) => ({
    feature: feat.feature,
    attribution: feat.importance,
    description: feat.description,
    isMonetary: feat.is_monetary,
  }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WhyFlaggedDrawer({
  anomalyId,
  onClose,
  isOpen,
  reportId,
  className,
}: WhyFlaggedDrawerProps) {
  const [activeTab, setActiveTab] = useState<TabId>('this-anomaly');

  // This anomaly state
  const [anomalyData, setAnomalyData] = useState<ExplainabilityResponse | null>(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyError, setAnomalyError] = useState<string | null>(null);

  // Global importance state
  const [globalData, setGlobalData] = useState<FeatureImportanceResponse | null>(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Fetch this anomaly explanation
  const fetchAnomalyExplanation = useCallback(async () => {
    if (!anomalyId || !isOpen) return;

    setAnomalyLoading(true);
    setAnomalyError(null);

    try {
      const response = await explainAnomaly({
        anomaly_id: anomalyId,
        // Optional: add feature_context if we have specific report context
        ...(reportId && { feature_context: { report_id: reportId } }),
      });
      setAnomalyData(response);
    } catch (err) {
      setAnomalyError(err instanceof Error ? err.message : 'Failed to load explanation');
    } finally {
      setAnomalyLoading(false);
    }
  }, [anomalyId, reportId, isOpen]);

  // Fetch global feature importance (cached/SWR pattern - fetch once)
  const fetchGlobalImportance = useCallback(async () => {
    if (!isOpen) return;

    setGlobalLoading(true);
    setGlobalError(null);

    try {
      const response = await getFeatureImportance();
      setGlobalData(response);
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : 'Failed to load feature importance');
    } finally {
      setGlobalLoading(false);
    }
  }, [isOpen]);

  // Fetch data when tab changes or drawer opens
  useEffect(() => {
    if (!isOpen) return;

    // Guard on the error states too: without `!anomalyError` / `!globalError` the
    // effect re-fires after a failed fetch (data is still null), retrying forever on a
    // persistent error — and clearing the error mid-flight so the UI flips back to the
    // empty state instead of showing the failure. Fetch once; the Retry button re-arms.
    if (activeTab === 'this-anomaly' && !anomalyData && !anomalyLoading && !anomalyError) {
      fetchAnomalyExplanation();
    } else if (activeTab === 'global-importance' && !globalData && !globalLoading && !globalError) {
      fetchGlobalImportance();
    }
  }, [activeTab, isOpen, anomalyData, globalData, anomalyLoading, globalLoading, anomalyError, globalError, fetchAnomalyExplanation, fetchGlobalImportance]);

  // Reset data when drawer closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to avoid visual flash before close animation
      const timer = setTimeout(() => {
        setAnomalyData(null);
        setAnomalyError(null);
        // Keep global data for caching, but could clear if desired
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderTabContent = () => {
    // This anomaly tab
    if (activeTab === 'this-anomaly') {
      if (anomalyLoading) {
        return (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--color-brand)] mb-4" />
            <p className="text-sm text-[var(--color-text-muted)]">
              Generating SHAP explanation...
            </p>
          </div>
        );
      }

      if (anomalyError) {
        return (
          <EmptyState
            icon={<HelpCircle className="w-10 h-10 text-[var(--color-text-ghost)]" />}
            title="Failed to load explanation"
            description={anomalyError}
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAnomalyExplanation}
                className="border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
              >
                Retry
              </Button>
            }
          />
        );
      }

      if (!anomalyData || anomalyData.attributions.length === 0) {
        return (
          <EmptyState
            icon={<HelpCircle className="w-10 h-10 text-[var(--color-text-ghost)]" />}
            title="No explanation available"
            description="SHAP attribution data is not available for this anomaly."
          />
        );
      }

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
            <span>Model version: {anomalyData.model_version}</span>
            <span>{new Date(anomalyData.generated_at).toLocaleString()}</span>
          </div>

          <ShapBar
            items={explainabilityToShapItems(anomalyData)}
            title="Feature attributions"
            maxItems={10}
          />

          <div
            className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-base)] p-4"
          >
            <h5
              className="text-xs font-medium mb-2"
              style={{
                fontFamily: 'Outfit, system-ui, sans-serif',
                color: 'var(--color-text-secondary)',
              }}
            >
              How to read this
            </h5>
            <p
              className="text-xs leading-relaxed"
              style={{ color: 'var(--color-text-muted)' }}
            >
              SHAP values show how each feature contributed to flagging this anomaly.
              Positive values (orange) increase the likelihood of the flag,
              while negative values (gray) decrease it. The length of each bar
              indicates the magnitude of the contribution.
            </p>
          </div>
        </div>
      );
    }

    // Global importance tab
    if (globalLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-brand)] mb-4" />
          <p className="text-sm text-[var(--color-text-muted)]">
            Loading feature importance...
          </p>
        </div>
      );
    }

    if (globalError) {
      return (
        <EmptyState
          icon={<HelpCircle className="w-10 h-10 text-[var(--color-text-ghost)]" />}
          title="Failed to load feature importance"
          description={globalError}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={fetchGlobalImportance}
              className="border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
            >
              Retry
            </Button>
          }
        />
      );
    }

    if (!globalData || globalData.features.length === 0) {
      return (
        <EmptyState
          icon={<HelpCircle className="w-10 h-10 text-[var(--color-text-ghost)]" />}
          title="No feature importance data"
          description="Global feature importance is not available yet."
        />
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
          <span>{globalData.total_features} total features · {globalData.sample_count} samples</span>
          <span>Model: {globalData.model_version}</span>
        </div>

        <ShapBar
          items={featureImportanceToShapItems(globalData)}
          title="Top features by importance"
          maxItems={10}
        />

        <div
          className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-base)] p-4"
        >
          <h5
            className="text-xs font-medium mb-2"
            style={{
              fontFamily: 'Outfit, system-ui, sans-serif',
              color: 'var(--color-text-secondary)',
            }}
          >
            About global importance
          </h5>
          <p
            className="text-xs leading-relaxed"
            style={{ color: 'var(--color-text-muted)' }}
          >
            These are the features that most strongly influence anomaly detection
            across all transactions. Higher importance means the feature has more
            predictive power in identifying anomalous patterns.
          </p>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className={cn(
          'w-full sm:max-w-lg p-0 gap-0 border-l border-[var(--color-border)]',
          'bg-[var(--color-bg-surface)]',
          className
        )}
        style={{ backgroundColor: 'var(--color-bg-surface)' }}
      >
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-[var(--color-border-sub)]">
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle
                className="text-lg font-semibold"
                style={{
                  fontFamily: 'DM Serif Display, Georgia, serif',
                  color: 'var(--color-text-primary)',
                }}
              >
                Why flagged?
              </SheetTitle>
              <SheetDescription
                className="text-xs mt-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Anomaly ID: <span className="font-mono text-[var(--color-text-secondary)]">{anomalyId}</span>
              </SheetDescription>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label="Close drawer"
            >
              <X className="w-5 h-5 text-[var(--color-text-muted)]" />
            </button>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border-sub)]">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors relative',
                activeTab === tab.id
                  ? 'text-[var(--color-brand)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              )}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.id && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: 'var(--color-brand)' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {renderTabContent()}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default WhyFlaggedDrawer;
